import Box from '@mui/joy/Box'
import Chip from '@mui/joy/Chip'
import Drawer from '@mui/joy/Drawer'
import Dropdown from '@mui/joy/Dropdown'
import GlobalStyles from '@mui/joy/GlobalStyles'
import IconButton from '@mui/joy/IconButton'
import Link from '@mui/joy/Link'
import List from '@mui/joy/List'
import ListItem from '@mui/joy/ListItem'
import ListItemButton from '@mui/joy/ListItemButton'
import MenuButton from '@mui/joy/MenuButton'
import ModalClose from '@mui/joy/ModalClose'
import Sheet from '@mui/joy/Sheet'
import Tooltip from '@mui/joy/Tooltip'
import Typography from '@mui/joy/Typography'
import { type ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  TbBulb,
  TbCalendarRepeat,
  TbHelp,
  TbMessageCircle,
  TbSettings,
  TbSquarePlus,
  TbStar,
  TbStarFilled,
} from 'react-icons/tb'
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { openChatwoot } from '../components/chatwoot/chatwootApi'
import CreditBalanceBadge from '../components/views/CreditBalanceBadge/CreditBalanceBadge'
import LogoIcon from '../components/views/LogoIcon/LogoIcon'
import LogoWordmark from '../components/views/LogoWordmark/LogoWordmark'
import useFeedbackPrompt from '../hooks/useFeedbackPrompt/useFeedbackPrompt'
import { useIsMobile } from '../hooks/useIsMobile'
import useSocialAccounts from '../hooks/useSocialAccounts'
import { useCampaignsStatisticsQuery } from '../store/endpoints/campaign'
import { useCreditBalanceQuery } from '../store/endpoints/credits'
import { usePostsStatisticsQuery } from '../store/endpoints/posts'
import { CAMPAIGN_STATUSES, POST_STATUSES } from '../types'
import { mapStatistic } from '../utils/mapStatistic'
import { useMobileMenu } from './mobileMenu/MobileMenuContext'
import { SIDEBAR_WIDTH, SIDEBAR_WIDTH_VAR } from './sidebar'

interface MenuItem {
  id: string
  icon: ReactElement | string
  path?: string
  href?: string
  titleKey: string
  tooltipKey?: string
  tooltipColor?: 'primary'
  pinned?: boolean
  onClick?: () => void
}

const TOP_MENU: MenuItem[] = [
  { id: 'posts', icon: <TbSquarePlus />, path: 'posts?status=SENT', titleKey: 'nav.posts' },
  {
    id: 'campaigns',
    icon: <TbCalendarRepeat />,
    path: 'campaigns?status=ACTIVE',
    titleKey: 'nav.campaigns',
  },
]
const BOTTOM_MENU: MenuItem[] = [
  {
    id: 'feature-requests',
    icon: <TbBulb />,
    href: 'https://amplorium.canny.io/features-requests',
    titleKey: 'nav.feature-requests',
  },
  {
    id: 'help-center',
    icon: <TbHelp />,
    href: 'https://help.amplorium.com',
    titleKey: 'nav.help-center',
  },
  { id: 'settings', icon: <TbSettings />, path: 'settings', titleKey: 'nav.settings' },
]

// On mobile the Chatwoot launcher bubble is hidden, so the chat is opened from the drawer.
const MOBILE_BOTTOM_MENU: MenuItem[] = BOTTOM_MENU.flatMap((item) =>
  item.id === 'help-center'
    ? [
        item,
        {
          id: 'support',
          icon: <TbMessageCircle />,
          titleKey: 'nav.support',
          onClick: openChatwoot,
        },
      ]
    : [item],
)

// First in the bottom group, per the design. Visible only until feedback has
// been given, and offered as an onClick-only item — the same shape the mobile
// support entry already uses.
const feedbackMenuItem = (onClick: () => void, icon: ReactElement): MenuItem => ({
  id: 'feedback',
  icon,
  titleKey: 'nav.feedback',
  onClick,
})

const useBottomMenu = (
  base: MenuItem[],
  feedbackIcon: ReactElement = <TbStarFilled />,
): MenuItem[] => {
  const { hasAnswered, requestPrompt } = useFeedbackPrompt()

  return hasAnswered ? base : [feedbackMenuItem(requestPrompt, feedbackIcon), ...base]
}

const getBasePathname = (location: { pathname: string }): string => location.pathname.split('/')[1]

const HeaderItem = ({
  id,
  icon,
  path,
  href,
  titleKey,
  tooltipKey,
  tooltipColor,
  pinned,
  onClick,
}: MenuItem): ReactElement => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()

  const basePathname = getBasePathname(location)
  const text = t(tooltipKey ?? titleKey)

  const menuButton = (
    <ListItem key={id} sx={{ p: 0 }}>
      <div>
        <Dropdown>
          <Tooltip
            color={tooltipColor}
            placement="right"
            arrow
            title={text}
            keepMounted
            open={pinned}
            sx={{
              width: typeof text === 'string' && text.length > 35 ? '160px' : 'unset',
              height: typeof text === 'string' && text.length > 35 ? '50px' : 'unset',
            }}
          >
            <MenuButton
              sx={{
                bgcolor:
                  path !== undefined &&
                  basePathname !== undefined &&
                  path.startsWith(basePathname[0])
                    ? 'neutral.plainActiveBg'
                    : undefined,
                color: 'text.icon',
                ':hover': {
                  bgcolor: 'neutral.plainHoverBg',
                  color: 'text.icon',
                },
              }}
              slots={{ root: IconButton }}
              slotProps={{ root: { variant: 'plain', color: 'neutral', size: 'xs' } }}
              variant="outlined"
              onClick={
                onClick ??
                (path !== undefined
                  ? () => {
                      navigate(path)
                    }
                  : undefined)
              }
            >
              {icon}
            </MenuButton>
          </Tooltip>
        </Dropdown>
      </div>
    </ListItem>
  )

  if (href !== undefined) {
    return (
      <Link key={id} href={href} target="_blank">
        {menuButton}
      </Link>
    )
  }

  if (path !== undefined) {
    return (
      <RouterLink key={id} to={`/${path}`}>
        {menuButton}
      </RouterLink>
    )
  }

  return menuButton
}

const DRAWER_ITEM_SX = { gap: 1, borderRadius: 'md', textDecoration: 'none' } as const

const DRAWER_ICON_COLUMN_WIDTH = 32

const MobileDrawerItem = ({
  item,
  isActive = false,
  isSecondary = false,
  onClose,
}: {
  item: MenuItem
  isActive?: boolean
  isSecondary?: boolean
  onClose: () => void
}): ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleClick = (): void => {
    if (item.onClick !== undefined) {
      item.onClick()
    } else if (item.path !== undefined) {
      navigate(`/${item.path}`)
    }
    onClose()
  }

  const content = (
    <>
      <Box
        sx={{
          width: DRAWER_ICON_COLUMN_WIDTH,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: isSecondary ? 'lg' : 'xl',
          color: isActive ? 'primary.solidBg' : 'text.icon',
        }}
      >
        {item.icon}
      </Box>
      <Typography
        level={isSecondary ? 'body-sm' : 'title-md'}
        sx={{
          color: isSecondary && !isActive ? 'text.secondary' : 'text.primary',
        }}
      >
        {t(item.titleKey)}
      </Typography>
    </>
  )

  if (item.href !== undefined) {
    return (
      <ListItemButton
        component="a"
        href={item.href}
        target="_blank"
        onClick={onClose}
        sx={DRAWER_ITEM_SX}
      >
        {content}
      </ListItemButton>
    )
  }

  return (
    <ListItemButton onClick={handleClick} sx={DRAWER_ITEM_SX}>
      {content}
    </ListItemButton>
  )
}

const DesktopHeader = (): ReactElement => {
  const { socialAccounts, isLoading } = useSocialAccounts()
  const bottomMenu = useBottomMenu(BOTTOM_MENU)
  const { data: creditBalance } = useCreditBalanceQuery()
  const totalCredits = creditBalance?.totalCredits
  const isEmptySocialAccounts = !isLoading && (socialAccounts?.length ?? 0) === 0

  return (
    <Sheet
      sx={{
        pt: '21px',
        px: 2,
        pb: 3,
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 0,
        flexShrink: 0,
        gap: 3,
        width: SIDEBAR_WIDTH,
      }}
    >
      <GlobalStyles styles={{ ':root': { [SIDEBAR_WIDTH_VAR]: `${SIDEBAR_WIDTH}px` } }} />
      <RouterLink to="/">
        <LogoIcon />
      </RouterLink>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          alignItems: 'center',
          gap: 3,
        }}
      >
        <List key="top" sx={{ p: 0, display: 'flex', flexGrow: 1, gap: 2 }}>
          {TOP_MENU.map(HeaderItem)}
        </List>
        <List key="bottom" sx={{ p: 0, flexGrow: 0, gap: 2 }}>
          {typeof totalCredits === 'number' && (
            <ListItem sx={{ p: 0 }}>
              <CreditBalanceBadge balance={totalCredits} />
            </ListItem>
          )}
          {bottomMenu.map((item) => (
            <HeaderItem
              key={item.id}
              {...(item.id === 'settings'
                ? {
                    ...item,
                    tooltipColor: isEmptySocialAccounts ? 'primary' : undefined,
                    pinned: isEmptySocialAccounts,
                    tooltipKey: isEmptySocialAccounts ? 'nav.settings-tooltip' : undefined,
                  }
                : item)}
            />
          ))}
        </List>
      </Box>
    </Sheet>
  )
}

interface StatusSubMenuItem {
  id: string
  label: string
  itemsCount?: number
}

interface StatusChipProps {
  color: 'danger' | 'neutral'
  variant: 'soft' | 'plain'
  sx?: { color: string }
}

const getStatusChipProps = (status: string, itemsCount: number): StatusChipProps => {
  if (itemsCount === 0) {
    return { color: 'neutral', variant: 'plain', sx: { color: 'text.tertiary' } }
  }

  if (status === POST_STATUSES.FAILED) {
    return { color: 'danger', variant: 'soft' }
  }

  return { color: 'neutral', variant: 'soft' }
}

const MobileStatusSubMenu = ({
  items,
  activeStatus,
  hideCounters,
  onStatusChange,
}: {
  items: StatusSubMenuItem[]
  activeStatus: string | null
  hideCounters?: boolean
  onStatusChange: (status: string) => void
}): ReactElement => (
  <List
    sx={{
      p: 0,
      pl: `${DRAWER_ICON_COLUMN_WIDTH}px`,
      gap: 0.5,
      flexGrow: 0,
      '--ListItem-minHeight': '32px',
      '--ListItem-paddingX': '8px',
      '--ListItem-paddingY': '3px',
      '--ListItem-radius': (theme) => theme.vars.radius.md,
      '--ListItem-gap': '12px',
    }}
  >
    {items.map((item) => {
      const isActive = activeStatus === item.id
      const itemsCount = item.itemsCount ?? 0
      const { sx: chipSx, ...chipProps } = getStatusChipProps(item.id, itemsCount)

      return (
        <ListItem key={item.id}>
          <ListItemButton
            selected={isActive}
            onClick={() => {
              onStatusChange(item.id)
            }}
          >
            <Typography level="body-sm" sx={{ flex: 1, color: 'text.primary' }}>
              {item.label}
            </Typography>
            {hideCounters !== true && (
              <Chip
                size="sm"
                color={chipProps.color}
                variant={chipProps.variant}
                sx={{
                  '--Chip-minHeight': '21px',
                  '--Chip-paddingInline': '7px',
                  '--Chip-radius': (theme) => theme.vars.radius.lg,
                  fontSize: 'sm',
                  fontWeight: 'md',
                  ...chipSx,
                }}
              >
                {itemsCount}
              </Chip>
            )}
          </ListItemButton>
        </ListItem>
      )
    })}
  </List>
)

const MobileMenuDrawer = (): ReactElement | null => {
  const { t } = useTranslation()
  const mobileBottomMenu = useBottomMenu(MOBILE_BOTTOM_MENU, <TbStar />)
  const menu = useMobileMenu()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const basePathname = getBasePathname(location)
  const currentStatus =
    searchParams.get('status') ??
    (basePathname === 'posts'
      ? POST_STATUSES.SENT
      : basePathname === 'campaigns'
        ? CAMPAIGN_STATUSES.ACTIVE
        : null)

  const { data: postsStatistics } = usePostsStatisticsQuery()
  const { data: campaignsStatistics } = useCampaignsStatisticsQuery()

  const hidePostsCounters =
    basePathname === 'posts' &&
    (Boolean(searchParams.get('search')) ||
      Boolean(searchParams.get('campaign')) ||
      Boolean(searchParams.get('social')) ||
      Boolean(searchParams.get('type')))

  const postsStatusItems = useMemo((): StatusSubMenuItem[] => {
    const statuses = [
      { id: POST_STATUSES.SENT, label: t('posts.status.published') },
      { id: POST_STATUSES.SCHEDULED, label: t('posts.status.scheduled') },
      { id: POST_STATUSES.FAILED, label: t('posts.status.failed') },
      { id: POST_STATUSES.DRAFT, label: t('posts.status.draft') },
    ]

    if (hidePostsCounters || postsStatistics == null) {
      return statuses
    }

    return mapStatistic(statuses, postsStatistics)
  }, [postsStatistics, hidePostsCounters, t])

  const campaignsStatusItems = useMemo((): StatusSubMenuItem[] => {
    const statuses = [
      { id: CAMPAIGN_STATUSES.ACTIVE, label: t('campaigns.status.active') },
      { id: CAMPAIGN_STATUSES.PAUSED, label: t('campaigns.status.paused') },
    ]

    if (campaignsStatistics == null) {
      return statuses
    }

    return mapStatistic(statuses, campaignsStatistics)
  }, [campaignsStatistics, t])

  const handlePostsStatusChange = (status: string): void => {
    navigate(`/posts?status=${status}`)
    menu?.close()
  }

  const handleCampaignsStatusChange = (status: string): void => {
    navigate(`/campaigns?status=${status}`)
    menu?.close()
  }

  const closeDrawer = (): void => {
    menu?.close()
  }

  const activePostsStatus = basePathname === 'posts' ? currentStatus : null
  const activeCampaignsStatus = basePathname === 'campaigns' ? currentStatus : null

  if (menu === null) {
    return null
  }

  return (
    <Drawer
      anchor="right"
      open={menu.isOpen}
      onClose={closeDrawer}
      size="sm"
      slotProps={{
        content: {
          sx: {
            width: 216,
            maxWidth: '80vw',
            bgcolor: 'background.body',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          pl: 2,
          pr: 1,
          pt: 1.5,
        }}
      >
        <Box
          component={RouterLink}
          to="/"
          aria-label={t('nav.home')}
          onClick={closeDrawer}
          sx={{ display: 'flex', alignItems: 'center', color: 'text.primary' }}
        >
          <LogoWordmark />
        </Box>
        <ModalClose sx={{ position: 'static' }} />
      </Box>
      <List
        sx={{
          px: 2,
          py: 3,
          gap: 1,
          flexGrow: 1,
          overflow: 'hidden auto',
          '--ListItem-minHeight': '32px',
          '--ListItem-paddingX': '0px',
          '--ListItem-paddingY': '0px',
          '--ListItem-gap': '8px',
        }}
      >
        <MobileDrawerItem
          item={TOP_MENU[0]}
          isActive={basePathname === 'posts'}
          onClose={closeDrawer}
        />
        <MobileStatusSubMenu
          items={postsStatusItems}
          activeStatus={activePostsStatus}
          hideCounters={hidePostsCounters}
          onStatusChange={handlePostsStatusChange}
        />
        <MobileDrawerItem
          item={TOP_MENU[1]}
          isActive={basePathname === 'campaigns'}
          onClose={closeDrawer}
        />
        <MobileStatusSubMenu
          items={campaignsStatusItems}
          activeStatus={activeCampaignsStatus}
          onStatusChange={handleCampaignsStatusChange}
        />
        <Box sx={{ mt: 'auto' }} />
        {mobileBottomMenu.map((item) => (
          <MobileDrawerItem
            key={item.id}
            item={item}
            isActive={item.path !== undefined && basePathname === item.path}
            isSecondary
            onClose={closeDrawer}
          />
        ))}
      </List>
    </Drawer>
  )
}

const Header = (): ReactElement | null => {
  const isMobile = useIsMobile()

  if (isMobile) {
    return <MobileMenuDrawer />
  }

  return <DesktopHeader />
}

export default Header
